import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import {
  TrendingUp, Database, Brain, Clock, ArrowUpRight, ArrowDownRight,
  Shield, AlertTriangle, Activity, Globe, Briefcase, Zap, Radio,
  DollarSign, BarChart3, Layers, Eye
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const OLIVE = "#A8A67A";
const SECTOR_COLORS = {
  Technology: "#A8A67A", Financial: "#8E8C64", Energy: "#6B7C6B",
  Healthcare: "#C4C29A", Index: "#4A5A4A", Consumer: "#D4D2B0",
  Industrial: "#9CAF88", Materials: "#B5B38A"
};

/* ════════════════════════════════════════════════════════════════════
   MOCK DATA — Bloomberg / Reuters / Refinitiv / Morningstar
   ════════════════════════════════════════════════════════════════════ */

const BLOOMBERG_INDICES = [
  { name: "S&P/ASX 200", value: 8124.60, change: 0.82, source: "Bloomberg" },
  { name: "S&P 500", value: 5892.34, change: 0.47, source: "Bloomberg" },
  { name: "NASDAQ", value: 19432.10, change: 1.12, source: "Reuters" },
  { name: "FTSE 100", value: 8412.80, change: -0.23, source: "Refinitiv" },
  { name: "Nikkei 225", value: 39812.50, change: 0.65, source: "Bloomberg" },
  { name: "DAX", value: 18654.20, change: 0.31, source: "Reuters" },
  { name: "Hang Seng", value: 18234.70, change: -0.41, source: "Bloomberg" },
  { name: "Euro Stoxx 50", value: 5124.30, change: 0.19, source: "Refinitiv" },
];

const BLOOMBERG_COMMODITIES = [
  { name: "Gold (XAU)", value: 2648.30, change: 0.54, unit: "USD/oz" },
  { name: "Brent Crude", value: 78.42, change: -1.21, unit: "USD/bbl" },
  { name: "WTI Crude", value: 74.18, change: -0.98, unit: "USD/bbl" },
  { name: "Silver (XAG)", value: 31.24, change: 0.82, unit: "USD/oz" },
  { name: "Iron Ore", value: 108.60, change: 1.34, unit: "USD/t" },
  { name: "Copper", value: 9234.50, change: 0.42, unit: "USD/t" },
];

const BLOOMBERG_FOREX = [
  { pair: "AUD/USD", rate: 0.6742, change: -0.18 },
  { pair: "EUR/USD", rate: 1.0832, change: 0.12 },
  { pair: "GBP/USD", rate: 1.2714, change: 0.08 },
  { pair: "USD/JPY", rate: 154.82, change: 0.34 },
  { pair: "AUD/EUR", rate: 0.6224, change: -0.06 },
  { pair: "NZD/USD", rate: 0.6134, change: -0.22 },
];

const BLOOMBERG_BONDS = [
  { name: "US 10Y Treasury", yield: 4.28, change: 0.03 },
  { name: "AU 10Y Govt", yield: 4.42, change: -0.02 },
  { name: "US 2Y Treasury", yield: 4.18, change: 0.01 },
  { name: "UK 10Y Gilt", yield: 4.34, change: 0.05 },
  { name: "DE 10Y Bund", yield: 2.38, change: -0.01 },
  { name: "JP 10Y JGB", yield: 1.08, change: 0.02 },
];

/* ── Ellerston Funds Performance ─────────────────────────────────── */
const ELLERSTON_FUNDS = [
  { name: "Ellerston Australian Absolute Return Fund", code: "EAARF", nav: 1.4832, inception: "2009", aum: "412M",
    m1: 1.82, m3: 4.21, ytd: 9.34, y1: 14.82, y3: 8.41, since: 9.12, rating: 5, category: "Long-Short Equity" },
  { name: "Ellerston Global Equity Managers Fund", code: "EGEMF", nav: 2.1247, inception: "2017", aum: "286M",
    m1: 2.34, m3: 6.12, ytd: 15.67, y1: 22.34, y3: 12.18, since: 11.42, rating: 4, category: "Global Equity" },
  { name: "Ellerston Australian Emerging Leaders", code: "EAELF", nav: 1.8421, inception: "2018", aum: "198M",
    m1: 3.12, m3: 7.84, ytd: 18.42, y1: 26.14, y3: 14.52, since: 13.28, rating: 5, category: "Small/Mid Cap" },
  { name: "Ellerston Asia Growth Fund", code: "EAGF", nav: 1.2134, inception: "2019", aum: "156M",
    m1: -0.42, m3: 2.84, ytd: 8.92, y1: 12.64, y3: 6.82, since: 7.24, rating: 3, category: "Asian Equity" },
  { name: "Ellerston India Fund", code: "EIF", nav: 1.6842, inception: "2020", aum: "124M",
    m1: 1.24, m3: 5.42, ytd: 14.28, y1: 19.82, y3: 11.24, since: 14.82, rating: 4, category: "India Equity" },
  { name: "Ellerston 2050 Fund", code: "E2050", nav: 1.3214, inception: "2021", aum: "89M",
    m1: 2.84, m3: 8.12, ytd: 21.42, y1: 28.64, y3: null, since: 16.42, rating: 4, category: "Growth" },
  { name: "Ellerston Global Mid Small Cap Fund", code: "EGMSC", nav: 1.5642, inception: "2019", aum: "172M",
    m1: 1.98, m3: 5.84, ytd: 12.84, y1: 18.42, y3: 10.84, since: 11.84, rating: 4, category: "Global SMID" },
  { name: "Ellerston Equity Income KIS Fund", code: "EEIKF", nav: 1.1284, inception: "2018", aum: "234M",
    m1: 0.92, m3: 2.64, ytd: 6.42, y1: 9.84, y3: 7.24, since: 7.84, rating: 3, category: "Equity Income" },
];

/* ── Fund NAV history for sparklines ─────────────────────────────── */
const genSparkline = (base, volatility, trend) =>
  Array.from({ length: 30 }, (_, i) => ({
    d: i,
    v: +(base + (trend * i * 0.001) + (Math.sin(i * 0.7) * volatility) + (Math.random() - 0.5) * volatility * 0.5).toFixed(4),
  }));

const FUND_SPARKLINES = {
  EAARF: genSparkline(1.46, 0.008, 1.2),
  EGEMF: genSparkline(2.08, 0.012, 1.8),
  EAELF: genSparkline(1.78, 0.015, 2.4),
  EAGF: genSparkline(1.20, 0.010, 0.6),
  EIF: genSparkline(1.65, 0.012, 1.4),
  E2050: genSparkline(1.28, 0.014, 2.8),
  EGMSC: genSparkline(1.52, 0.011, 1.6),
  EEIKF: genSparkline(1.11, 0.006, 0.8),
};

const MARKET_MOVERS = [
  { ticker: "NVDA", change: 4.2, price: 892.50, name: "NVIDIA Corp" },
  { ticker: "AAPL", change: 1.8, price: 198.30, name: "Apple Inc" },
  { ticker: "MSFT", change: 1.2, price: 428.60, name: "Microsoft" },
  { ticker: "TSLA", change: -2.4, price: 242.10, name: "Tesla Inc" },
  { ticker: "AMZN", change: 0.9, price: 186.70, name: "Amazon" },
];

const RISK_METRICS = [
  { label: "Sharpe Ratio", value: "1.84", status: "good" },
  { label: "Max Drawdown", value: "-8.2%", status: "warning" },
  { label: "Beta", value: "0.92", status: "good" },
  { label: "VaR (95%)", value: "$186K", status: "neutral" },
];

const MONTHLY_PERF = [
  { month: "Jan", portfolio: 2.1, benchmark: 1.8 },
  { month: "Feb", portfolio: -0.8, benchmark: -1.2 },
  { month: "Mar", portfolio: 3.2, benchmark: 2.9 },
  { month: "Apr", portfolio: 1.5, benchmark: 0.8 },
  { month: "May", portfolio: 4.1, benchmark: 3.5 },
  { month: "Jun", portfolio: -1.2, benchmark: -0.5 },
  { month: "Jul", portfolio: 2.8, benchmark: 2.1 },
  { month: "Aug", portfolio: 0.6, benchmark: 1.0 },
  { month: "Sep", portfolio: -0.4, benchmark: -1.8 },
  { month: "Oct", portfolio: 3.6, benchmark: 2.7 },
  { month: "Nov", portfolio: 2.2, benchmark: 1.9 },
  { month: "Dec", portfolio: 1.9, benchmark: 1.4 },
];

const AUM_HISTORY = [
  { month: "Jul", aum: 4.12 }, { month: "Aug", aum: 4.18 },
  { month: "Sep", aum: 4.25 }, { month: "Oct", aum: 4.41 },
  { month: "Nov", aum: 4.52 }, { month: "Dec", aum: 4.68 },
  { month: "Jan", aum: 4.71 }, { month: "Feb", aum: 4.65 },
  { month: "Mar", aum: 4.82 }, { month: "Apr", aum: 4.88 },
  { month: "May", aum: 4.95 }, { month: "Jun", aum: 4.99 },
];

/* ═══════════════════ COMPONENTS ═══════════════════ */

function CandlestickChart({ data }) {
  if (!data || data.length === 0) return null;
  const W = 880, H = 200, PAD_L = 50, PAD_R = 10, PAD_T = 10, PAD_B = 30;
  const innerW = W - PAD_L - PAD_R, innerH = H - PAD_T - PAD_B;
  const prices = data.flatMap(d => [d.high, d.low]);
  const minP = Math.min(...prices) * 0.995, maxP = Math.max(...prices) * 1.005;
  const toY = v => PAD_T + innerH - ((v - minP) / (maxP - minP)) * innerH;
  const candleW = Math.max(2, innerW / data.length - 2);
  const xLabels = data.filter((_, i) => i % 6 === 0).map(d => ({
    x: PAD_L + (data.indexOf(d) / (data.length - 1)) * innerW, label: d.date?.slice(0, 7) || ""
  }));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: H }}>
      {[0, 0.25, 0.5, 0.75, 1].map(t => {
        const y = PAD_T + innerH * (1 - t), val = minP + t * (maxP - minP);
        return (<g key={t}><line x1={PAD_L} x2={W - PAD_R} y1={y} y2={y} stroke="#f1f5f9" strokeWidth={1} /><text x={PAD_L - 4} y={y + 4} textAnchor="end" fontSize={9} fill="#94a3b8">${val.toFixed(0)}</text></g>);
      })}
      {data.map((d, i) => {
        const cx = PAD_L + (i / (data.length - 1)) * innerW;
        const isUp = d.close >= d.open, color = isUp ? OLIVE : "#ef4444";
        const bodyTop = toY(Math.max(d.open, d.close)), bodyBot = toY(Math.min(d.open, d.close));
        return (<g key={i}><line x1={cx} x2={cx} y1={toY(d.high)} y2={toY(d.low)} stroke={color} strokeWidth={1} /><rect x={cx - candleW / 2} y={bodyTop} width={candleW} height={Math.max(1, bodyBot - bodyTop)} fill={color} stroke={color} strokeWidth={0.5} opacity={0.85} /></g>);
      })}
      {xLabels.map((l, i) => (<text key={i} x={l.x} y={H - 4} textAnchor="middle" fontSize={9} fill="#94a3b8">{l.label}</text>))}
    </svg>
  );
}

function StatCard({ label, value, sub, icon: Icon, positive, testId }) {
  return (
    <div className="card-olive bg-white border border-border p-5 hover:shadow-md transition-shadow" data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{label}</p>
        <Icon className="w-4 h-4 text-[#C4C29A]" strokeWidth={1.5} />
      </div>
      <p className="stat-value">{value}</p>
      {sub && (
        <div className="flex items-center gap-1 mt-2">
          {positive !== undefined && (positive ? <ArrowUpRight className="w-3.5 h-3.5 text-emerald-600" /> : <ArrowDownRight className="w-3.5 h-3.5 text-red-500" />)}
          <span className={`text-xs font-medium ${positive === true ? "text-emerald-600" : positive === false ? "text-red-500" : "text-muted-foreground"}`}>{sub}</span>
        </div>
      )}
    </div>
  );
}

function MiniSparkline({ data, color = OLIVE }) {
  return (
    <ResponsiveContainer width={80} height={28}>
      <LineChart data={data}><Line type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} dot={false} /></LineChart>
    </ResponsiveContainer>
  );
}

function StarRating({ count }) {
  return <span className="text-amber-400 text-[10px] tracking-tight">{"\u2605".repeat(count)}{"\u2606".repeat(5 - count)}</span>;
}

/* ── Live ticker hook ─────────────────────────────────────────────── */
function useLiveTicker(funds) {
  const [liveNav, setLiveNav] = useState(() => funds.reduce((a, f) => ({ ...a, [f.code]: f.nav }), {}));
  const [liveChanges, setLiveChanges] = useState(() => funds.reduce((a, f) => ({ ...a, [f.code]: 0 }), {}));

  useEffect(() => {
    const interval = setInterval(() => {
      setLiveNav(prev => {
        const next = { ...prev };
        const changes = {};
        funds.forEach(f => {
          const delta = (Math.random() - 0.48) * 0.003 * f.nav;
          next[f.code] = +(prev[f.code] + delta).toFixed(4);
          changes[f.code] = delta;
        });
        setLiveChanges(changes);
        return next;
      });
    }, 3000);
    return () => clearInterval(interval);
  }, [funds]);

  return { liveNav, liveChanges };
}

function fmtMoney(v) {
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  return `$${v.toLocaleString()}`;
}

/* ═══════════════════ MAIN DASHBOARD ═══════════════════ */
export default function Dashboard() {
  const [prices, setPrices] = useState([]);
  const [portfolio, setPortfolio] = useState({ data: [], total_aum: 0, total_pnl: 0 });
  const [candleData, setCandleData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllHoldings, setShowAllHoldings] = useState(false);
  const { liveNav, liveChanges } = useLiveTicker(ELLERSTON_FUNDS);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/data/prices/all`),
      axios.get(`${API}/data/portfolio`),
      axios.get(`${API}/data/prices?year_from=2022&year_to=2024&limit=36`),
    ]).then(([pa, pf, cd]) => {
      setPrices(pa.data.data);
      setPortfolio(pf.data);
      setCandleData(cd.data.data);
    }).finally(() => setLoading(false));
  }, []);

  const sectors = portfolio.data.reduce((acc, h) => { acc[h.sector] = (acc[h.sector] || 0) + h.portfolio_weight; return acc; }, {});
  const sectorPie = Object.entries(sectors).map(([name, value]) => ({ name, value: +value.toFixed(1) }));
  const topHoldings = [...portfolio.data].sort((a, b) => b.unrealized_pnl - a.unrealized_pnl).slice(0, 8).map(h => ({ name: h.ticker, pnl: h.unrealized_pnl }));
  const recentPrices = prices.slice(-20).map(p => ({ year: p.year, close: p.avg_close, ret: +p.annual_return }));
  const pnlPct = portfolio.total_aum ? ((portfolio.total_pnl / (portfolio.total_aum - portfolio.total_pnl)) * 100).toFixed(1) : "0.0";
  const displayedHoldings = showAllHoldings ? portfolio.data : portfolio.data.slice(0, 8);

  if (loading) {
    return (<div className="flex items-center justify-center h-full"><div className="flex gap-2 dot-pulse"><span /><span /><span /></div></div>);
  }

  return (
    <div className="page-enter p-6 space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">Ellerston Capital</p>
          <h1 className="text-4xl font-bold text-foreground tracking-tight">Intelligence Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Real-time portfolio analytics &amp; market intelligence</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-xs text-emerald-600 bg-emerald-50 px-2.5 py-1 border border-emerald-200">
            <Radio className="w-3 h-3 animate-pulse" />
            <span className="font-semibold">LIVE</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted px-3 py-1.5 border border-border">
            <Clock className="w-3.5 h-3.5" />
            <span>Last sync: {new Date().toLocaleTimeString()}</span>
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="kpi-grid">
        <StatCard label="Total AUM" value={fmtMoney(portfolio.total_aum)} sub="15 active positions" icon={TrendingUp} testId="stat-aum" />
        <StatCard label="Unrealized P&L" value={fmtMoney(portfolio.total_pnl)} sub={`${pnlPct}% total return`} positive={portfolio.total_pnl >= 0} icon={ArrowUpRight} testId="stat-pnl" />
        <StatCard label="Data Records" value="600+" sub="50 years S&P 500" icon={Database} testId="stat-records" />
        <StatCard label="Time to Insight" value="<2s" sub="vs 45–90 min (VBA)" positive={true} icon={Brain} testId="stat-tti" />
      </div>

      {/* ══════════════════════════════════════════════════════════════
         BLOOMBERG / MARKET DATA TERMINAL
         ══════════════════════════════════════════════════════════════ */}
      <div className="card-olive bg-[#0a0a0a] border border-white/10 p-0 overflow-hidden" data-testid="bloomberg-terminal">
        <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Zap className="w-4 h-4 text-amber-400" />
            <span className="text-xs font-bold text-white uppercase tracking-wider">Market Data Terminal</span>
            <span className="text-[9px] text-white/30 font-mono">Bloomberg · Reuters · Refinitiv</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[9px] text-emerald-400 font-mono">LIVE FEED</span>
          </div>
        </div>

        {/* Global Indices */}
        <div className="px-5 py-3 border-b border-white/5">
          <p className="text-[9px] text-amber-400/60 uppercase tracking-widest font-semibold mb-2">Global Indices</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2">
            {BLOOMBERG_INDICES.map(idx => (
              <div key={idx.name} className="flex items-center justify-between py-1">
                <div>
                  <p className="text-[11px] text-white/90 font-medium">{idx.name}</p>
                  <p className="text-[9px] text-white/30">{idx.source}</p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] text-white font-mono font-semibold">{idx.value.toLocaleString()}</p>
                  <p className={`text-[10px] font-mono font-semibold ${idx.change >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {idx.change >= 0 ? "+" : ""}{idx.change}%
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Commodities + Forex + Bonds in 3 columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/5">
          {/* Commodities */}
          <div className="px-5 py-3">
            <p className="text-[9px] text-amber-400/60 uppercase tracking-widest font-semibold mb-2">Commodities</p>
            {BLOOMBERG_COMMODITIES.map(c => (
              <div key={c.name} className="flex items-center justify-between py-1.5">
                <span className="text-[10px] text-white/70">{c.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-white font-mono">{c.value.toLocaleString()}</span>
                  <span className={`text-[9px] font-mono font-semibold min-w-[42px] text-right ${c.change >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {c.change >= 0 ? "+" : ""}{c.change}%
                  </span>
                </div>
              </div>
            ))}
          </div>
          {/* Forex */}
          <div className="px-5 py-3">
            <p className="text-[9px] text-amber-400/60 uppercase tracking-widest font-semibold mb-2">Foreign Exchange</p>
            {BLOOMBERG_FOREX.map(f => (
              <div key={f.pair} className="flex items-center justify-between py-1.5">
                <span className="text-[10px] text-white/70 font-mono">{f.pair}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-white font-mono">{f.rate.toFixed(4)}</span>
                  <span className={`text-[9px] font-mono font-semibold min-w-[42px] text-right ${f.change >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {f.change >= 0 ? "+" : ""}{f.change}%
                  </span>
                </div>
              </div>
            ))}
          </div>
          {/* Bonds */}
          <div className="px-5 py-3">
            <p className="text-[9px] text-amber-400/60 uppercase tracking-widest font-semibold mb-2">Government Bonds</p>
            {BLOOMBERG_BONDS.map(b => (
              <div key={b.name} className="flex items-center justify-between py-1.5">
                <span className="text-[10px] text-white/70">{b.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-white font-mono">{b.yield.toFixed(2)}%</span>
                  <span className={`text-[9px] font-mono font-semibold min-w-[42px] text-right ${b.change >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {b.change >= 0 ? "+" : ""}{b.change.toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════
         LIVE FUND VALUATION TRACKER
         ══════════════════════════════════════════════════════════════ */}
      <div className="card-olive bg-white border border-border overflow-hidden" data-testid="live-fund-tracker">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between bg-[#FAFAFA]">
          <div className="flex items-center gap-2">
            <Radio className="w-4 h-4 text-emerald-500 animate-pulse" />
            <div>
              <p className="text-xs font-bold uppercase tracking-wider">Live Fund Valuations</p>
              <p className="text-[10px] text-muted-foreground">NAV updated every 3 seconds · Morningstar ratings</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-[10px] text-muted-foreground">Real-time tracker</span>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y divide-border">
          {ELLERSTON_FUNDS.map(f => {
            const navNow = liveNav[f.code] || f.nav;
            const delta = liveChanges[f.code] || 0;
            const isUp = delta >= 0;
            return (
              <div key={f.code} className="p-4 hover:bg-[#FAFAFA] transition-colors" data-testid={`live-fund-${f.code}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-mono font-bold text-[#A8A67A]">{f.code}</span>
                  <StarRating count={f.rating} />
                </div>
                <p className="text-[10px] text-muted-foreground truncate mb-2" title={f.name}>{f.name}</p>
                <div className="flex items-end justify-between">
                  <div>
                    <p className={`text-xl font-bold font-mono transition-colors duration-300 ${isUp ? "text-emerald-600" : "text-red-500"}`}>
                      ${navNow.toFixed(4)}
                    </p>
                    <p className={`text-[10px] font-mono font-semibold flex items-center gap-0.5 ${isUp ? "text-emerald-600" : "text-red-500"}`}>
                      {isUp ? <ArrowUpRight className="w-2.5 h-2.5" /> : <ArrowDownRight className="w-2.5 h-2.5" />}
                      {isUp ? "+" : ""}{(delta * 100 / f.nav).toFixed(3)}%
                    </p>
                  </div>
                  <MiniSparkline data={FUND_SPARKLINES[f.code]} color={isUp ? "#10b981" : "#ef4444"} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════
         ELLERSTON TOP FUNDS PERFORMANCE TABLE
         ══════════════════════════════════════════════════════════════ */}
      <div className="card-olive bg-white border border-border overflow-hidden" data-testid="funds-performance">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-[#A8A67A]" />
            <h3 className="text-sm font-semibold">Ellerston Funds — Performance Overview</h3>
          </div>
          <span className="text-[10px] bg-[#F5F4EE] text-[#A8A67A] px-2 py-1 font-semibold uppercase tracking-wide">Source: Morningstar</span>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Fund</th><th>Category</th><th className="text-right">NAV</th><th className="text-right">AUM</th>
                <th className="text-right">1M</th><th className="text-right">3M</th><th className="text-right">YTD</th>
                <th className="text-right">1Y</th><th className="text-right">3Y (p.a.)</th><th className="text-right">Since Incep.</th>
                <th className="text-center">Rating</th>
              </tr>
            </thead>
            <tbody>
              {ELLERSTON_FUNDS.map(f => (
                <tr key={f.code}>
                  <td>
                    <p className="font-semibold text-foreground text-xs">{f.name}</p>
                    <p className="text-[10px] text-muted-foreground font-mono">{f.code} · Est. {f.inception}</p>
                  </td>
                  <td><span className="badge-synced" style={{ background: "#f2f4f0", color: "#A8A67A" }}>{f.category}</span></td>
                  <td className="text-right font-mono font-semibold">${f.nav.toFixed(4)}</td>
                  <td className="text-right font-mono">${f.aum}</td>
                  <td className={`text-right font-mono font-semibold ${f.m1 >= 0 ? "text-emerald-600" : "text-red-500"}`}>{f.m1 >= 0 ? "+" : ""}{f.m1}%</td>
                  <td className={`text-right font-mono font-semibold ${f.m3 >= 0 ? "text-emerald-600" : "text-red-500"}`}>{f.m3 >= 0 ? "+" : ""}{f.m3}%</td>
                  <td className={`text-right font-mono font-semibold ${f.ytd >= 0 ? "text-emerald-600" : "text-red-500"}`}>{f.ytd >= 0 ? "+" : ""}{f.ytd}%</td>
                  <td className={`text-right font-mono font-semibold ${f.y1 >= 0 ? "text-emerald-600" : "text-red-500"}`}>{f.y1 >= 0 ? "+" : ""}{f.y1}%</td>
                  <td className="text-right font-mono">{f.y3 != null ? `${f.y3}%` : "—"}</td>
                  <td className={`text-right font-mono font-semibold text-[#A8A67A]`}>{f.since}%</td>
                  <td className="text-center"><StarRating count={f.rating} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* AUM Growth + Risk Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 card-olive bg-white border border-border p-5" data-testid="aum-growth-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">AUM Growth</p>
              <h3 className="text-base font-semibold mt-0.5">12-Month Trend</h3>
            </div>
            <span className="text-xs font-semibold text-emerald-600 flex items-center gap-1"><ArrowUpRight className="w-3 h-3" /> +21.1% YoY</span>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={AUM_HISTORY} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
              <defs><linearGradient id="aumGradient" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={OLIVE} stopOpacity={0.3} /><stop offset="95%" stopColor={OLIVE} stopOpacity={0.02} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94a3b8" }} />
              <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={v => `$${v}M`} domain={['dataMin - 0.1', 'dataMax + 0.1']} />
              <Tooltip formatter={(v) => [`$${v.toFixed(2)}M`, "AUM"]} contentStyle={{ fontSize: 11, border: "1px solid #e2e8f0", borderRadius: 2 }} />
              <Area type="monotone" dataKey="aum" stroke={OLIVE} strokeWidth={2} fill="url(#aumGradient)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="card-olive bg-white border border-border p-5" data-testid="risk-metrics">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-4 h-4 text-[#A8A67A]" strokeWidth={1.5} />
            <div><p className="text-xs text-muted-foreground uppercase tracking-wider">Risk Analytics</p><h3 className="text-base font-semibold mt-0.5">Key Metrics</h3></div>
          </div>
          <div className="space-y-3">
            {RISK_METRICS.map(m => (
              <div key={m.label} className="flex items-center justify-between p-3 bg-[#FAFAFA] border border-border">
                <div className="flex items-center gap-2">
                  {m.status === "good" && <div className="w-2 h-2 rounded-full bg-emerald-500" />}
                  {m.status === "warning" && <AlertTriangle className="w-3 h-3 text-amber-500" />}
                  {m.status === "neutral" && <Activity className="w-3 h-3 text-blue-500" />}
                  <span className="text-xs text-muted-foreground">{m.label}</span>
                </div>
                <span className="text-sm font-semibold font-mono">{m.value}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 p-3 bg-[#F5F4EE] border border-[#D4D2B0]/30">
            <p className="text-[10px] text-muted-foreground"><strong className="text-[#A8A67A]">Portfolio Health:</strong> Within target risk parameters. Beta below 1.0 indicates defensive positioning.</p>
          </div>
        </div>
      </div>

      {/* Performance Comparison + Market Movers */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 card-olive bg-white border border-border p-5" data-testid="perf-comparison">
          <div className="flex items-center justify-between mb-4">
            <div><p className="text-xs text-muted-foreground uppercase tracking-wider">Performance Comparison</p><h3 className="text-base font-semibold mt-0.5">Portfolio vs S&P 500 Benchmark</h3></div>
            <div className="flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-1 bg-[#A8A67A]" /> Portfolio</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-1 bg-[#D4D2B0]" /> Benchmark</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={MONTHLY_PERF} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94a3b8" }} />
              <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={v => `${v}%`} />
              <Tooltip formatter={(v) => [`${Number(v).toFixed(1)}%`]} contentStyle={{ fontSize: 11, border: "1px solid #e2e8f0", borderRadius: 2 }} />
              <Bar dataKey="portfolio" fill={OLIVE} name="Portfolio" radius={[2, 2, 0, 0]} />
              <Bar dataKey="benchmark" fill="#D4D2B0" name="Benchmark" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border">
            <div className="text-center flex-1"><p className="text-[10px] text-muted-foreground uppercase tracking-wider">Portfolio YTD</p><p className="text-lg font-bold text-emerald-600">+19.6%</p></div>
            <div className="w-px h-8 bg-border" />
            <div className="text-center flex-1"><p className="text-[10px] text-muted-foreground uppercase tracking-wider">Benchmark YTD</p><p className="text-lg font-bold text-muted-foreground">+14.6%</p></div>
            <div className="w-px h-8 bg-border" />
            <div className="text-center flex-1"><p className="text-[10px] text-muted-foreground uppercase tracking-wider">Alpha</p><p className="text-lg font-bold text-[#A8A67A]">+5.0%</p></div>
          </div>
        </div>
        <div className="card-olive bg-white border border-border p-5" data-testid="market-movers">
          <div className="flex items-center gap-2 mb-4"><Globe className="w-4 h-4 text-[#A8A67A]" strokeWidth={1.5} /><div><p className="text-xs text-muted-foreground uppercase tracking-wider">Market Movers</p><h3 className="text-base font-semibold mt-0.5">Today's Top Activity</h3></div></div>
          <div className="space-y-2">
            {MARKET_MOVERS.map(m => (
              <div key={m.ticker} className="flex items-center justify-between p-3 border border-border hover:bg-[#FAFAFA] transition-colors">
                <div><p className="text-xs font-semibold font-mono text-[#A8A67A]">{m.ticker}</p><p className="text-[10px] text-muted-foreground">{m.name}</p></div>
                <div className="text-right">
                  <p className="text-xs font-mono font-semibold">${m.price.toFixed(2)}</p>
                  <p className={`text-[10px] font-semibold flex items-center justify-end gap-0.5 ${m.change >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                    {m.change >= 0 ? <ArrowUpRight className="w-2.5 h-2.5" /> : <ArrowDownRight className="w-2.5 h-2.5" />}
                    {m.change >= 0 ? "+" : ""}{m.change}%
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* S&P 500 + Sector Pie */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 card-olive bg-white border border-border p-5" data-testid="sp500-chart">
          <div className="flex items-center justify-between mb-4">
            <div><p className="text-xs text-muted-foreground uppercase tracking-wider">S&P 500 Annual Returns</p><h3 className="text-base font-semibold mt-0.5">Last 20 Years</h3></div>
            <span className="text-[10px] bg-[#F5F4EE] text-[#A8A67A] px-2 py-1 font-semibold uppercase tracking-wide">2005–2024</span>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={recentPrices} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="year" tick={{ fontSize: 10, fill: "#94a3b8" }} />
              <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={v => `${v.toFixed(0)}%`} />
              <Tooltip formatter={(v) => [`${Number(v).toFixed(2)}%`, "Annual Return"]} contentStyle={{ fontSize: 11, border: "1px solid #e2e8f0", borderRadius: 2 }} />
              <Bar dataKey="ret" radius={0}>{recentPrices.map((entry, i) => (<Cell key={i} fill={entry.ret >= 0 ? OLIVE : "#ef4444"} />))}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card-olive bg-white border border-border p-5" data-testid="sector-chart">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Portfolio Allocation</p>
          <h3 className="text-base font-semibold mb-3">By Sector</h3>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart><Pie data={sectorPie} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" nameKey="name" paddingAngle={2}>{sectorPie.map((entry, i) => (<Cell key={i} fill={SECTOR_COLORS[entry.name] || "#ccc"} />))}</Pie><Tooltip formatter={(v) => [`${v.toFixed(1)}%`]} contentStyle={{ fontSize: 11 }} /></PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {sectorPie.map(s => (
              <div key={s.name} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full" style={{ background: SECTOR_COLORS[s.name] || "#ccc" }} /><span className="text-foreground">{s.name}</span></div>
                <span className="font-mono font-semibold text-[#A8A67A]">{s.value.toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Candlestick + Holdings P&L */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-olive bg-white border border-border p-5" data-testid="candlestick-chart">
          <div className="flex items-center justify-between mb-2">
            <div><p className="text-xs text-muted-foreground uppercase tracking-wider">S&P 500 OHLC</p><h3 className="text-base font-semibold">2022–2024 Monthly Candlestick</h3></div>
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-[#A8A67A]" /> Up</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-red-500" /> Down</span>
            </div>
          </div>
          <CandlestickChart data={candleData} />
        </div>
        <div className="card-olive bg-white border border-border p-5" data-testid="holdings-pnl-chart">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Portfolio P&L</p>
          <h3 className="text-base font-semibold mb-3">Top Holdings — Unrealized Gain</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={topHoldings} layout="vertical" margin={{ top: 0, right: 16, left: 20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 9, fill: "#94a3b8" }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: "#374151" }} width={45} />
              <Tooltip formatter={(v) => [`$${Number(v).toLocaleString()}`, "P&L"]} contentStyle={{ fontSize: 11, border: "1px solid #e2e8f0", borderRadius: 2 }} />
              <Bar dataKey="pnl" radius={0}>{topHoldings.map((entry, i) => (<Cell key={i} fill={entry.pnl >= 0 ? OLIVE : "#ef4444"} />))}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Full Portfolio Holdings Table */}
      <div className="card-olive bg-white border border-border" data-testid="holdings-table">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2"><Briefcase className="w-4 h-4 text-[#A8A67A]" /><h3 className="text-sm font-semibold">Portfolio Holdings</h3></div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">{portfolio.data.length} positions</span>
            <button
              onClick={() => setShowAllHoldings(!showAllHoldings)}
              className="text-[10px] font-semibold text-[#A8A67A] hover:text-[#8E8C64] underline transition-colors"
              data-testid="toggle-all-holdings"
            >
              {showAllHoldings ? "Show Less" : "Show All"}
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Ticker</th><th>Company</th><th>Sector</th>
                <th className="text-right">Shares</th><th className="text-right">Avg Cost</th>
                <th className="text-right">Current Price</th><th className="text-right">Mkt Value</th>
                <th className="text-right">Unreal. P&L</th><th className="text-right">Weight</th>
              </tr>
            </thead>
            <tbody>
              {displayedHoldings.map(h => (
                <tr key={h.ticker} data-testid={`holding-row-${h.ticker}`}>
                  <td className="font-mono font-semibold text-[#A8A67A]">{h.ticker}</td>
                  <td className="text-foreground">{h.company_name}</td>
                  <td><span className="badge-synced" style={{ background: "#f2f4f0", color: "#A8A67A" }}>{h.sector}</span></td>
                  <td className="text-right font-mono">{h.shares.toLocaleString()}</td>
                  <td className="text-right font-mono">${h.purchase_price.toLocaleString()}</td>
                  <td className="text-right font-mono">${h.current_price.toLocaleString()}</td>
                  <td className="text-right font-mono">${h.market_value.toLocaleString()}</td>
                  <td className={`text-right font-mono font-semibold ${h.unrealized_pnl >= 0 ? "text-emerald-600" : "text-red-500"}`}>${h.unrealized_pnl.toLocaleString()}</td>
                  <td className="text-right font-mono">{h.portfolio_weight.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!showAllHoldings && portfolio.data.length > 8 && (
          <div className="px-4 py-2 border-t border-border bg-[#FAFAFA] text-center">
            <button onClick={() => setShowAllHoldings(true)} className="text-xs text-[#A8A67A] hover:text-[#8E8C64] font-semibold">
              + {portfolio.data.length - 8} more positions — Click to expand
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
